/**
 * Priority fee cache with 30s refresh and stale guard.
 *
 * Mirrors `BlockhashCache`: background refresh on init, interval, and
 * foreground-resume; `get()` serves fresh values; explicit `refresh()` re-throws
 * on Helius/network failure for caller-controlled fallback.
 *
 * Platform-neutral — safe in src/auth/ core.
 */

import type { Connection } from '@solana/web3.js';

import type { TxEngineLogger, TxOnForeground } from '../config.js';
import { isValidTransactionTraceId } from '../diagnostics/lifecycle.js';
import type { CachedPriorityFee } from '../types.js';
import { fetchPriorityFeeEstimate } from './priority-fee.js';

const REFRESH_INTERVAL_MS = 30_000;
const STALE_THRESHOLD_MS = 60_000;

export class PriorityFeeCache {
  private cached: CachedPriorityFee | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private connection: Connection | null = null;
  private refreshFailed = false;
  private foregroundUnsub: (() => void) | null = null;
  private readonly logger: TxEngineLogger;
  private readonly onForeground: TxOnForeground | undefined;
  private readonly network: string;

  constructor(logger: TxEngineLogger, network: string, onForeground?: TxOnForeground) {
    this.logger = logger;
    this.network = network;
    this.onForeground = onForeground;
  }

  /** Start the automatic refresh interval. */
  start(connection: Connection): void {
    this.connection = connection;
    this.scheduleBackgroundRefresh(connection);

    this.intervalId = setInterval(() => {
      /* v8 ignore start -- defensive: stop() clears the interval before nulling connection, so the false branch never fires */
      if (this.connection) {
        this.scheduleBackgroundRefresh(this.connection);
      }
      /* v8 ignore stop */
    }, REFRESH_INTERVAL_MS);

    if (this.onForeground) {
      this.foregroundUnsub = this.onForeground(() => {
        if (this.connection) {
          this.scheduleBackgroundRefresh(this.connection);
        }
      });
    }
  }

  /** Stop the automatic refresh interval. */
  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.foregroundUnsub) {
      this.foregroundUnsub();
      this.foregroundUnsub = null;
    }
    this.connection = null;
    this.cached = null;
    this.refreshFailed = false;
  }

  /** Get the cached priority fee if fresh (within 60s and no refresh failure). */
  get(): CachedPriorityFee | null {
    if (!this.cached) {
      return null;
    }

    if (this.refreshFailed) {
      return null;
    }

    const age = Date.now() - this.cached.fetchedAt;
    if (age > STALE_THRESHOLD_MS) {
      return null;
    }

    return this.cached;
  }

  /** Force-refresh the cached priority fee. Re-throws on Helius/network failure. */
  async refresh(connection: Connection, traceId?: string): Promise<CachedPriorityFee> {
    const safeTraceId = isValidTransactionTraceId(traceId) ? traceId : undefined;
    try {
      const feeMicrolamports = await fetchPriorityFeeEstimate(
        connection,
        this.network,
        this.logger,
        safeTraceId,
      );
      this.cached = {
        feeMicrolamports,
        network: this.network,
        fetchedAt: Date.now(),
      };
      this.refreshFailed = false;
      return this.cached;
    } catch (e: unknown) {
      this.refreshFailed = true;
      this.logger.warn('tx.priority_fee.refresh_failed', {
        network: this.network,
        errorClass: e instanceof Error ? 'Error' : typeof e,
        ...(safeTraceId === undefined ? {} : { traceId: safeTraceId }),
      });
      throw e;
    }
  }

  /**
   * Fire-and-forget refresh for interval / foreground paths. `refresh()` logs,
   * sets `refreshFailed`, and re-throws for explicit await callers; here we
   * swallow the rejection so background work cannot surface as unhandled.
   */
  private scheduleBackgroundRefresh(connection: Connection): void {
    void this.refresh(connection).catch(() => {
      // State + logging handled inside refresh().
    });
  }
}
